/**
 * KioskCheckout - Checkout screen with partner code, coupon code, and Cash payment
 * Note: M-PAiSA and EFTPOS options are only available at the POS counter
 */
import { useState } from 'react';
import { ChevronLeft, Tag, Crown, Gift, CheckCircle, X, Loader2, Ticket, Banknote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useKiosk, LOYALTY_TIERS } from './KioskContext';
import KioskCouponInput from './KioskCouponInput';
import api from '../../utils/apiClient';

export default function KioskCheckout() {
  const {
    setView, isAccessible, buttonSizeClass, iconClass, largeIconClass,
    customer, cart, cartTotal, partnerCode, setPartnerCode,
    partnerCodeValid, partnerInfo, validatingCode,
    loyaltyDiscount, partnerDiscount, couponDiscount, totalDiscountPercent,
    discountAmount, vatAmount,
    finalTotal, setOrderNumber, setPaymentStatus,
    couponCode, setCouponCode, handleValidCoupon
  } = useKiosk();

  // Cash payment state
  const [cashOrderProcessing, setCashOrderProcessing] = useState(false);

  // Handle Cash - Pay at Cashier
  const handleCashPayment = async () => {
    setCashOrderProcessing(true);
    try {
      const orderData = {
        items: cart.map(item => ({
          item_id: item.item_id || item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          category: item.category || null,
          // Include ALL customization data for station printing
          size: item.size || item.selectedSize || null,
          selectedSize: item.selectedSize || item.size || null,
          modifiers: item.modifiers || null,
          special_instructions: item.specialNotes || item.special_instructions || null,
          specialNotes: item.specialNotes || item.special_instructions || null,
          removed_ingredients: item.removedIngredients || item.removed_ingredients || null,
          removedIngredients: item.removedIngredients || item.removed_ingredients || null,
          extra_ingredients: item.extraIngredients || item.extra_ingredients || null,
          extraIngredients: item.extraIngredients || item.extra_ingredients || null,
          add_ons: item.addOns || item.add_ons || null,
          addOns: item.addOns || item.add_ons || null
        })),
        customer_name: customer?.first_name ? `${customer.first_name} ${customer.last_name || ''}`.trim() : 'Kiosk Guest',
        customer_id: customer?.contact_id || customer?.id,
        customer_phone: customer?.phone,
        customer_email: customer?.email,
        order_type: 'kiosk',
        total: finalTotal,
        subtotal: cartTotal,
        discount: discountAmount,
        discount_reason: totalDiscountPercent > 0 ? `${partnerDiscount > 0 ? 'Partner ' + partnerDiscount + '%' : ''}${loyaltyDiscount > 0 ? ' Loyalty ' + loyaltyDiscount + '%' : ''}`.trim() : null,
        status: 'pending_payment',           // Order created but not paid
        payment_status: 'unpaid',            // Needs to be paid at cashier
        payment_method: 'cash_pending',      // Cash at cashier
        partner_code: partnerCodeValid ? partnerCode : null
      };

      const res = await api.post('/orders', orderData);
      const order = res.data;
      
      // PRINT TO KIOSK PRINTER - Customer takes this receipt to cashier
      try {
        await api.post(`/receipts/kiosk-print?order_id=${order.id || order.order_id}`);
        console.log('Kiosk receipt printed for cash order');
      } catch (printErr) {
        console.error('Kiosk receipt print failed:', printErr);
        // Don't block order flow if print fails
      }
      
      // Set payment status for confirmation screen
      if (typeof setPaymentStatus === 'function') {
        setPaymentStatus('cash_pending');
      }
      
      // Set order number and go to confirmation
      if (typeof setOrderNumber === 'function') {
        setOrderNumber(order.order_number || order.id?.slice(0, 8).toUpperCase());
      }
      setView('confirmation');
      
    } catch (err) {
      console.error('Cash order creation error:', err);
      alert('Failed to create order. Please try again.');
    } finally {
      setCashOrderProcessing(false);
    }
  };

  // Handle Free Coffee Pass Redemption (no payment needed)
  const handleFreeRedemption = async () => {
    setCashOrderProcessing(true);
    try {
      // Find the coffee pass redemption item in cart (check both field names)
      const redemptionItem = cart.find(item => item.isCoffeeClubRedemption || item.isCoffeePassRedemption);
      
      // Check if this is a PURE free redemption (only the free coffee, no upsells, no extra items)
      // Pure redemption = only 1 item in cart AND it's a coffee pass redemption AND price is $0
      const isPureFreeRedemption = cart.length === 1 && 
        redemptionItem && 
        (redemptionItem.price === 0 || finalTotal === 0);
      
      const orderData = {
        items: cart.map(item => ({
          item_id: item.item_id || item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          category: item.category || 'coffee',
          // Include ALL customization data for station printing
          size: item.size || item.selectedSize || null,
          selectedSize: item.selectedSize || item.size || null,
          modifiers: item.modifiers || null,
          special_instructions: item.specialNotes || item.special_instructions || null,
          specialNotes: item.specialNotes || item.special_instructions || null,
          removed_ingredients: item.removedIngredients || item.removed_ingredients || null,
          removedIngredients: item.removedIngredients || item.removed_ingredients || null,
          extra_ingredients: item.extraIngredients || item.extra_ingredients || null,
          extraIngredients: item.extraIngredients || item.extra_ingredients || null,
          add_ons: item.addOns || item.add_ons || null,
          addOns: item.addOns || item.add_ons || null,
          is_coffee_pass_redemption: item.isCoffeeClubRedemption || item.isCoffeePassRedemption || false
        })),
        customer_name: customer?.first_name ? `${customer.first_name} ${customer.last_name || ''}`.trim() : 'Coffee Pass Member',
        customer_id: customer?.contact_id || customer?.id,
        customer_phone: customer?.phone,
        customer_email: customer?.email,
        order_type: 'kiosk',
        total: finalTotal,
        subtotal: cartTotal,
        vat_amount: 0,
        discount: discountAmount,
        // If pure free redemption: mark as confirmed (bypasses POS)
        // If has upsells/extras: mark as pending_confirmation (goes to POS queue)
        status: isPureFreeRedemption ? 'confirmed' : 'pending_confirmation',
        payment_status: 'paid',             // Mark as paid (it's free or prepaid upgrade)
        payment_method: 'coffee_pass',      // Special payment method for tracking
        is_coffee_pass_redemption: true,
        is_pure_free_redemption: isPureFreeRedemption,
        partner_code: partnerCodeValid ? partnerCode : null
      };

      const res = await api.post('/orders', orderData);
      const order = res.data;
      
      // Note: Coffee Pass redemption was already recorded when item was added to cart
      // via KioskCoffeeClubRedemption.jsx calling /coffee-pass/redeem
      
      // PRINT based on redemption type
      if (isPureFreeRedemption) {
        // PURE FREE REDEMPTION: Print directly to Kiosk + Barista (bypasses POS)
        // Customer takes kiosk receipt directly to barista counter
        try {
          await api.post(`/receipts/coffee-pass-direct-print?order_id=${order.id || order.order_id}`);
          console.log('Coffee Pass DIRECT print: Kiosk + Barista (bypasses POS)');
        } catch (printErr) {
          console.error('Coffee Pass direct print failed:', printErr);
        }
      } else {
        // HAS UPSELLS OR EXTRAS: Print to Kiosk only, goes to POS queue
        try {
          await api.post(`/receipts/kiosk-print?order_id=${order.id || order.order_id}`);
          console.log('Kiosk receipt printed - order goes to POS queue for payment');
        } catch (printErr) {
          console.error('Kiosk receipt print failed:', printErr);
        }
      }
      
      // Set payment status for confirmation screen
      if (typeof setPaymentStatus === 'function') {
        setPaymentStatus(isPureFreeRedemption ? 'coffee_pass_direct' : 'coffee_pass');
      }
      
      // Set order number and go to confirmation
      if (typeof setOrderNumber === 'function') {
        setOrderNumber(order.order_number || order.id?.slice(0, 8).toUpperCase());
      }
      setView('confirmation');
      
    } catch (err) {
      console.error('Free redemption order error:', err);
      alert('Failed to create order. Please try again.');
    } finally {
      setCashOrderProcessing(false);
    }
  };

  // Get customer tier info
  const customerTier = customer?.loyalty_tier?.toLowerCase() || 'new_member';
  const tierInfo = LOYALTY_TIERS[customerTier] || LOYALTY_TIERS.new_member;

  return (
    <div className="min-h-screen bg-gray-950 text-white p-8" data-testid="kiosk-checkout">
      <Button 
        variant="ghost" 
        onClick={() => setView('cart')}
        className={`mb-8 text-white ${buttonSizeClass}`}
      >
        <ChevronLeft className={iconClass} />
        <span className={`ml-2 ${isAccessible ? 'text-xl' : ''}`}>Back to Cart</span>
      </Button>

      <div className="max-w-2xl mx-auto">
        <h1 className={`${isAccessible ? 'text-5xl' : 'text-4xl'} font-bold mb-8 text-center`}>Checkout</h1>

        {/* Customer Info Banner */}
        {customer && (
          <div className="bg-gradient-to-r from-amber-600/30 to-amber-500/20 rounded-2xl p-6 mb-8 border border-amber-500/30">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-amber-500/30 flex items-center justify-center">
                <Crown className="w-8 h-8 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} font-bold text-amber-200`}>
                  {customer.first_name} {customer.last_name}
                </p>
                <p className="text-amber-400 capitalize">{tierInfo.name} Member</p>
                {loyaltyDiscount > 0 && (
                  <p className="text-green-400 text-sm mt-1">
                    <Gift className="w-4 h-4 inline mr-1" />
                    {loyaltyDiscount}% {tierInfo.name} discount applied!
                  </p>
                )}
              </div>
              {customer.total_points > 0 && (
                <div className="text-right">
                  <p className="text-amber-300 text-sm">Points Balance</p>
                  <p className="text-2xl font-bold text-amber-200">{customer.total_points || 0}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Partner Referral Code */}
        <div className="bg-gray-900 rounded-3xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Tag className={isAccessible ? 'w-8 h-8' : 'w-6 h-6'} />
            <label className={`${isAccessible ? 'text-2xl' : 'text-xl'}`}>Partner Referral Code</label>
            <span className="text-xs bg-teal-600 px-2 py-1 rounded-full">5% Off!</span>
          </div>
          <p className={`text-gray-400 ${isAccessible ? 'text-xl' : 'text-base'} mb-4`}>
            Got a code from a taxi driver or tour guide? Enter it for 5% off!
          </p>
          <div className="relative">
            <input
              type="text"
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value.toUpperCase())}
              placeholder="e.g., BULA-XXXX"
              className={`w-full px-6 ${isAccessible ? 'py-6 text-3xl' : 'py-4 text-2xl'} bg-gray-800 border-2 rounded-2xl text-white placeholder-gray-500 focus:outline-none uppercase ${
                partnerCodeValid === true ? 'border-green-500' :
                partnerCodeValid === false ? 'border-red-500' : 'border-gray-700'
              }`}
              data-testid="kiosk-partner-code-input"
            />
            {validatingCode && (
              <Loader2 className={`absolute right-4 top-1/2 -translate-y-1/2 ${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} animate-spin text-gray-400`} />
            )}
            {!validatingCode && partnerCodeValid === true && (
              <CheckCircle className={`absolute right-4 top-1/2 -translate-y-1/2 ${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} text-green-500`} />
            )}
            {!validatingCode && partnerCodeValid === false && partnerCode.length >= 4 && (
              <X className={`absolute right-4 top-1/2 -translate-y-1/2 ${isAccessible ? 'w-8 h-8' : 'w-6 h-6'} text-red-500`} />
            )}
          </div>
          {partnerCodeValid === true && partnerInfo && (
            <div className={`mt-4 p-4 bg-teal-900/50 rounded-xl border border-teal-700 ${isAccessible ? 'text-xl' : 'text-base'}`}>
              <span className="text-teal-400">{partnerInfo.message}</span>
            </div>
          )}
        </div>

        {/* Coupon Code Input */}
        <KioskCouponInput
          value={couponCode}
          onChange={setCouponCode}
          onValidCoupon={handleValidCoupon}
          isAccessible={isAccessible}
        />

        {/* Order Summary */}
        <div className="bg-gray-900 rounded-3xl p-8 mb-8">
          <h2 className={`${isAccessible ? 'text-3xl' : 'text-2xl'} font-bold mb-4`}>Order Summary</h2>
          <div className="space-y-4 mb-6">
            {cart.map((item, idx) => (
              <div key={`${item.item_id}-${idx}`} className={`flex justify-between ${isAccessible ? 'text-2xl' : 'text-xl'}`}>
                <div className="flex-1">
                  <span>{item.quantity}x {item.name}</span>
                  {item.selectedSize && !item.is_coffee_pass && <span className="text-gray-400 text-sm ml-2">({item.selectedSize})</span>}
                  {item.is_coffee_pass && item.pass_number === 1 && (
                    <span className="text-green-400 text-sm ml-2">(Your Pass)</span>
                  )}
                  {item.is_coffee_pass && item.pass_number > 1 && (
                    <span className="text-purple-400 text-sm ml-2">(Gift #{item.pass_number - 1})</span>
                  )}
                </div>
                <span>{(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <div className={`flex justify-between ${isAccessible ? 'text-2xl' : 'text-xl'}`}>
              <span>Subtotal</span>
              <span>{cartTotal.toFixed(2)}</span>
            </div>
            {totalDiscountPercent > 0 && (
              <div className="space-y-2">
                {partnerDiscount > 0 && (
                  <div className={`flex justify-between ${isAccessible ? 'text-xl' : 'text-lg'} text-teal-400`}>
                    <span className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      Partner Discount ({partnerDiscount}%)
                    </span>
                    <span>-{(cartTotal * partnerDiscount / 100).toFixed(2)}</span>
                  </div>
                )}
                {loyaltyDiscount > 0 && (
                  <div className={`flex justify-between ${isAccessible ? 'text-xl' : 'text-lg'} text-amber-400`}>
                    <span className="flex items-center gap-2">
                      <Crown className="w-4 h-4" />
                      {tierInfo.name} Discount ({loyaltyDiscount}%)
                    </span>
                    <span>-{(cartTotal * loyaltyDiscount / 100).toFixed(2)}</span>
                  </div>
                )}
                {couponDiscount > 0 && (
                  <div className={`flex justify-between ${isAccessible ? 'text-xl' : 'text-lg'} text-purple-400`}>
                    <span className="flex items-center gap-2">
                      <Ticket className="w-4 h-4" />
                      Coupon Discount ({couponDiscount}%)
                    </span>
                    <span>-${(cartTotal * couponDiscount / 100).toFixed(2)}</span>
                  </div>
                )}
                <div className={`flex justify-between ${isAccessible ? 'text-2xl' : 'text-xl'} text-green-400 font-medium pt-1`}>
                  <span>Total Savings</span>
                  <span>-{discountAmount.toFixed(2)}</span>
                </div>
              </div>
            )}
            {/* VAT Included Note - Prices already include VAT */}
            <div className={`flex justify-between ${isAccessible ? 'text-xl' : 'text-lg'} text-gray-400`}>
              <span>12.5% VAT Included</span>
              <span>(${vatAmount.toFixed(2)})</span>
            </div>
            <div className={`flex justify-between ${isAccessible ? 'text-4xl' : 'text-3xl'} font-bold pt-2 border-t border-gray-700`}>
              <span>Total</span>
              <span className="text-green-400">{finalTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Section - Cash at Kiosk, Other options available at POS Counter */}

        {/* FREE ORDER - Coffee Pass Redemption Only */}
        {finalTotal <= 0.01 && (
          <div className="bg-gradient-to-r from-amber-600/30 to-amber-500/20 rounded-3xl p-8 mb-8 border-2 border-amber-400/50">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Gift className={`${isAccessible ? 'w-12 h-12' : 'w-10 h-10'} text-amber-400`} />
              <span className={`${isAccessible ? 'text-3xl' : 'text-2xl'} text-amber-200 font-bold`}>FREE Coffee Pass Redemption!</span>
            </div>
            
            <div className="text-center mb-6">
              <div className="bg-black/30 rounded-xl p-6 mb-4">
                <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-white mb-3`}>
                  Your free coffee is ready to order!
                </p>
                <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-gray-300`}>
                  Click the button below to confirm your order.
                </p>
                <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-amber-400 mt-3 font-semibold`}>
                  Take your order number to the counter - no payment needed!
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleFreeRedemption}
              disabled={cashOrderProcessing}
              className={`w-full ${isAccessible ? 'py-10 text-3xl' : 'py-8 text-2xl'} bg-amber-600 hover:bg-amber-700 rounded-2xl font-bold`}
            >
              {cashOrderProcessing ? (
                <>
                  <Loader2 className={`${largeIconClass} animate-spin mr-4`} />
                  Processing...
                </>
              ) : (
                <>
                  <Gift className={`${largeIconClass} mr-4`} />
                  Redeem My Free Coffee
                </>
              )}
            </Button>
          </div>
        )}

        {/* Cash - Pay at Cashier Section (only for paid orders) */}
        {finalTotal > 0.01 && (
          <div className="bg-green-900/30 rounded-3xl p-8 mb-8 border border-green-500/30">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Banknote className={`${isAccessible ? 'w-10 h-10' : 'w-8 h-8'} text-green-400`} />
              <span className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-green-200 font-bold`}>Pay at Counter</span>
            </div>
            
            <div className="text-center mb-6">
              <p className={`${isAccessible ? 'text-2xl' : 'text-xl'} text-white mb-2`}>
                Total: <span className="font-bold">${finalTotal.toFixed(2)}</span>
              </p>
              <div className="bg-black/30 rounded-xl p-4 mt-4">
                <p className={`${isAccessible ? 'text-xl' : 'text-lg'} text-gray-300`}>
                  Click below to place your order.
                </p>
                <p className={`${isAccessible ? 'text-lg' : 'text-base'} text-gray-400 mt-2`}>
                  You&apos;ll receive an <span className="text-white font-bold">Order Number</span>.
                  Take this number to the <span className="text-amber-400 font-bold">Cashier</span> to complete your payment.
                </p>
                <p className={`${isAccessible ? 'text-base' : 'text-sm'} text-gray-500 mt-2`}>
                  Cash, EFTPOS, or M-PAiSA accepted at the counter
                </p>
              </div>
            </div>
            
            <Button
              onClick={handleCashPayment}
              disabled={cashOrderProcessing}
              data-testid="kiosk-place-order-btn"
              className={`w-full ${isAccessible ? 'py-8 text-3xl' : 'py-6 text-2xl'} bg-green-600 hover:bg-green-700 rounded-2xl`}
            >
              {cashOrderProcessing ? (
                <>
                  <Loader2 className={`${largeIconClass} animate-spin mr-4`} />
                  Creating Order...
                </>
              ) : (
                <>
                  <CheckCircle className={`${largeIconClass} mr-4`} />
                  Place Order - Pay at Cashier
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
